import React, { useCallback } from 'react'
import { Button, Typography, Input, App } from 'antd'
import { TodayIconFont as IconFont } from '../../components/IconFonts'
import { useAIRecognition } from '../context/AIRecognitionContext'
import { useHazardItems } from '../hooks/useHazardItems'
import { useSensorTracking } from '../hooks/useSensorTracking'
import { selectUncheckedPhotos } from '../../../slices/photosByLocationSlice'
import { XCSelectionInfo } from '../types'
import AddHazardButton from './AddHazardButton'
import { DeleteOutlined } from '@ant-design/icons'

const { TextArea } = Input
const { Text } = Typography

/**
 * 等待识别组件 Props 接口
 */
interface RecognitionContentCompletedProps {
  onShowSafetyScenarioSelection?: (selectionInfo: XCSelectionInfo[]) => void
}

/**
 * AI识别结果组件
 */
const RecognitionContentCompleted: React.FC<RecognitionContentCompletedProps> =
  React.memo(({ onShowSafetyScenarioSelection }) => {
    const { message } = App.useApp()
    const {
      recognitionResult,
      updateReferenceStandard,
      selectedSafetyScenarios,
      setSafetyScenarioList,
      selectionInfo,
      setRememberChoice,
      setShowSafetyScenarioModal
    } = useAIRecognition()
    const { reportAISafetyHazardsClick } = useSensorTracking()
    const { updateHazardItem, deleteHazardItem } = useHazardItems()

    /**
     * 处理隐患项编辑
     */
    const handleEditHazard = useCallback(
      (index: number, content: string) => {
        // 上报编辑识别结果埋点
        reportAISafetyHazardsClick({ clickItem: 'editResult' })
        updateHazardItem(index, content)
      },
      [updateHazardItem, reportAISafetyHazardsClick]
    )

    /**
     * 处理隐患项删除
     */
    const handleDeleteHazard = useCallback(
      (index: number) => {
        // 上报删除识别结果埋点
        reportAISafetyHazardsClick({ clickItem: 'deleteOneResult' })
        deleteHazardItem(index)
        message.success('隐患项已删除')
      },
      [deleteHazardItem, reportAISafetyHazardsClick]
    )

    /**
     * 处理参考标准变更
     */
    const handleReferenceStandardChange = useCallback(
      (value: string) => {
        // 上报编辑参考标准埋点
        reportAISafetyHazardsClick({ clickItem: 'editStandard' })
        updateReferenceStandard([value])
      },
      [updateReferenceStandard, reportAISafetyHazardsClick]
    )

    // 如果没有识别结果，不渲染组件
    if (!recognitionResult) {
      return null
    }

    return (
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
          boxShadow: '-2px -1px 13.6px 0px rgba(233, 233, 233, 1)',
          position: 'relative',
          padding: '40px 19px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '476px',
          flex: 1
        }}
      >
        {/* 主要内容区域 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* 识别到的隐患标题和可编辑标签 - 仅在有隐患时显示 */}
          {recognitionResult.potentialHazardList &&
            recognitionResult.potentialHazardList.length > 0 && (
              <>
                <div className="section-title">
                  <Text className="title-text">识别到的隐患</Text>
                  <div className="editable-tag">
                    <Text className="tag-text">可编辑</Text>
                  </div>
                </div>

                {/* 隐患列表 */}
                <div style={{ marginBottom: '20px' }}>
                  {recognitionResult.potentialHazardList.map(
                    (hazard: string, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          marginBottom: '12px'
                        }}
                      >
                        {/* 输入框容器 */}
                        <TextArea
                          value={hazard}
                          onChange={(e) =>
                            handleEditHazard(index, e.target.value)
                          }
                          autoSize={{ minRows: 1, maxRows: 6 }}
                          placeholder="请输入隐患内容"
                          className="reference-textarea"
                          style={{
                            lineHeight: '26px',
                            fontWeight: 'bold',
                            fontSize: '17px',
                            paddingTop: '10px',
                            paddingBottom: '10px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px'
                          }}
                        />

                        {/* 删除按钮 */}
                        <div
                          onClick={() => handleDeleteHazard(index)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '50px'
                          }}
                        >
                          <DeleteOutlined
                            style={{
                              color: '#EA4D3D',
                              fontSize: '22px'
                            }}
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>
                {/* 补充遗漏的隐患，帮助AI训练  */}
                <AddHazardButton />
              </>
            )}

          {/* 参考标准区域 - 仅在有标准时显示 */}
          {recognitionResult.standardList &&
            recognitionResult.standardList.length > 0 && (
              <div className="reference-section">
                <div className="section-title">
                  <Text className="title-text">参考标准</Text>
                  <div className="editable-tag">
                    <Text className="tag-text">可编辑</Text>
                  </div>
                </div>
                <TextArea
                  value={recognitionResult.standardList?.join('\n') || ''}
                  onChange={(e) =>
                    handleReferenceStandardChange(e.target.value)
                  }
                  placeholder="参考标准将在这里显示..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  className="reference-textarea"
                />
              </div>
            )}

          {/* 思考过程区域 */}
          <div className="right-section">
            <div className="section-title">
              <Text className="title-text">思考过程</Text>
            </div>
            <div
              className="right-section-content"
              style={{
                maxHeight: '6.8em', // 约4行的高度 (17px * 1.5 * 4 = 102px)
                overflowY: 'auto',
                lineHeight: '1.5',
                color: '#444444'
              }}
            >
              {recognitionResult.thinking || '暂无思考过程'}
            </div>
          </div>
        </div>
      </div>
    )
  })

export default RecognitionContentCompleted
